import { AgChartOptions } from "ag-charts-community";

import { ANALYTICS_CUSTOM_SERIES_COLORS } from "../constants";
import type { CustomChartOptionsContext } from "../utils/customChartDataPrep";
import { isRecord } from "../utils/isRecord";

import { baseNonCartesianAnalyticsChartOptions } from "./base";
import { createAnalyticsTooltipResult, formatAxisValue } from "./customChartFormatters";

import {
  CustomChartType,
  type CustomChartResponseItem,
} from "@/queries/analytics-custom-charts-types";

export const buildPolarCustomChartOptions = (
  chart: CustomChartResponseItem,
  context?: CustomChartOptionsContext,
): AgChartOptions => {
  const unit = chart.series[0]?.unit ?? "number";
  const valueLabel = chart.series[0]?.label ?? "Value";
  const formatValue = (value: number) => formatAxisValue(unit, value);
  const pieData = chart.data
    .filter(row => Number(row.value ?? 0) > 0)
    .map((row, index) => ({
      label: row.label,
      rawLabel: row.rawLabel,
      value: Number(row.value ?? 0),
      color: ANALYTICS_CUSTOM_SERIES_COLORS[index % ANALYTICS_CUSTOM_SERIES_COLORS.length],
    }));

  const fills = pieData.map(entry => entry.color);
  const strokes = pieData.map(() => "#ffffff");
  const isDonut = chart.chartType === CustomChartType.Donut;

  return {
    ...baseNonCartesianAnalyticsChartOptions,
    legend: {
      enabled: true,
      item: {
        marker: {
          shape: "circle",
        },
      },
    },
    listeners: context?.onCategoryClick
      ? {
          seriesNodeClick: (event: unknown) => {
            if (!isRecord(event) || !isRecord(event.datum)) {
              return;
            }

            const { label } = event.datum;
            if (typeof label !== "string") {
              return;
            }

            const { rawLabel } = event.datum;
            context.onCategoryClick?.({
              label,
              rawLabel: typeof rawLabel === "string" ? rawLabel : undefined,
            });
          },
        }
      : undefined,
    data: pieData,
    series: [
      {
        type: isDonut ? ("donut" as const) : ("pie" as const),
        angleKey: "value",
        angleName: valueLabel,
        calloutLabelKey: "label",
        legendItemKey: "label",
        fills,
        strokes,
        strokeWidth: 1,
        sectorSpacing: 2,
        innerRadiusRatio: isDonut ? 0.55 : undefined,
        cursor: context?.onCategoryClick ? ("pointer" as const) : undefined,
        tooltip: {
          renderer: ({ datum }: { datum: { label: string; value: number; color: string } }) =>
            createAnalyticsTooltipResult({
              title: datum.label,
              symbolColor: datum.color,
              rows: [
                {
                  label: valueLabel,
                  value: formatValue(datum.value),
                  color: datum.color,
                },
              ],
            }),
        },
      },
    ],
  } as AgChartOptions;
};
